import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import merchantKycService from '../services/merchantKycService';
import MerchantKycProfile from './MerchantKycProfile';
import MerchantKycStatus from './MerchantKycStatus';
import MerchantKycDocuments from './MerchantKycDocuments';
import MerchantKycVerification from './MerchantKycVerification';
import './MerchantKycDashboard.css';

const MerchantKycDashboard = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('status');
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    initializeMerchantKyc();
  }, [currentUser]);

  // Add listener for KYC updates
  useEffect(() => {
    const handleKycUpdate = () => {
      console.log('🔄 MerchantKycDashboard: KYC update detected, refreshing profile');
      initializeMerchantKyc();
    };

    window.addEventListener('kycVerificationCompleted', handleKycUpdate);
    window.addEventListener('kycStatusUpdated', handleKycUpdate);

    return () => {
      window.removeEventListener('kycVerificationCompleted', handleKycUpdate);
      window.removeEventListener('kycStatusUpdated', handleKycUpdate);
    };
  }, [currentUser]);

  const initializeMerchantKyc = async () => {
    try {
      if (!currentUser) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      // Check if user is a merchant
      if (!merchantKycService.isMerchantEligibleForKyc(currentUser.role)) {
        setError('KYC verification is only available for merchant accounts');
        setLoading(false);
        return;
      }

      // Generate a unique merchant ID that includes user info to prevent collisions
      const merchantId = currentUser.id || currentUser.merchantId || currentUser.email;
      
      if (!merchantId) {
        setError('Unable to determine merchant ID');
        setLoading(false);
        return;
      }
      
      console.log('🔍 MerchantKycDashboard: Initializing for merchant:', merchantId, {
        userId: currentUser.id,
        merchantId: currentUser.merchantId,
        email: currentUser.email,
        role: currentUser.role
      });
      
      // Always get fresh profile data
      let profile = merchantKycService.getMerchantProfile(merchantId);
      if (!profile) {
        console.log('📝 Creating new merchant profile for:', merchantId);
        profile = merchantKycService.initializeMerchantProfile(merchantId, {
          fullName: currentUser.fullName || currentUser.name,
          email: currentUser.email,
          businessName: currentUser.businessName
        });
      } else {
        console.log('📊 Loaded existing profile for merchant:', merchantId, profile.kycStatus);
        
        // Verify the profile belongs to the correct merchant
        if (profile.merchantId !== merchantId) {
          console.warn('⚠️ Profile merchant ID mismatch! Expected:', merchantId, 'Got:', profile.merchantId);
          // Create a new profile for this merchant
          profile = merchantKycService.initializeMerchantProfile(merchantId, {
            fullName: currentUser.fullName || currentUser.name,
            email: currentUser.email,
            businessName: currentUser.businessName
          });
        }
        
        // CRITICAL: Check for existing verification results and sync with profile status
        const verification = merchantKycService.getMerchantVerification(merchantId);
        if (verification && verification.results) {
          console.log('🔍 Found existing verification results:', verification.results);
          
          // Use the new validation and sync function
          merchantKycService.validateAndSyncVerificationStatus(merchantId);
          
          // Reload profile after sync
          profile = merchantKycService.getMerchantProfile(merchantId);
        }
        
        // Recalculate completion percentage to ensure accuracy
        const currentCompletion = merchantKycService.calculateCompletionPercentage(merchantId);
        if (currentCompletion !== profile.kycStatus.completionPercentage) {
          console.log(`🔄 Updating completion from ${profile.kycStatus.completionPercentage}% to ${currentCompletion}%`);
          profile = merchantKycService.updateMerchantProfile(merchantId, {
            kycStatus: {
              ...profile.kycStatus,
              completionPercentage: currentCompletion
            }
          });
        }
      }

      setMerchantProfile(profile);
      setLoading(false);
    } catch (error) {
      console.error('Error initializing merchant KYC:', error);
      setError('Failed to initialize KYC dashboard');
      setLoading(false);
    }
  };

  const handleProfileUpdate = (updatedProfile) => {
    setMerchantProfile(updatedProfile);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (loading) {
    return (
      <div className="merchant-kyc-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading KYC Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="merchant-kyc-dashboard">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Access Restricted</h3>
          <p>{error}</p>
          {currentUser?.role !== 'MERCHANT' && (
            <div className="role-info">
              <p>Current role: <strong>{currentUser?.role || 'Unknown'}</strong></p>
              <p>KYC verification is exclusively for merchant accounts.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!merchantProfile) {
    return (
      <div className="merchant-kyc-dashboard">
        <div className="error-container">
          <div className="error-icon">❌</div>
          <h3>Profile Not Found</h3>
          <p>Unable to load merchant profile</p>
          <button 
            className="btn btn-primary"
            onClick={initializeMerchantKyc}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const getTabIcon = (tab) => {
    const icons = {
      status: '📊',
      profile: '👤',
      documents: '📄',
      verification: '🔍'
    };
    return icons[tab] || '📋';
  };

  const getStatusBadge = (status) => {
    const badges = {
      'NOT_INITIATED': { color: '#6c757d', text: 'Not Started' },
      'IN_PROGRESS': { color: '#ffc107', text: 'In Progress' },
      'PENDING_REVIEW': { color: '#17a2b8', text: 'Under Review' },
      'APPROVED': { color: '#28a745', text: 'Approved' },
      'REJECTED': { color: '#dc3545', text: 'Rejected' },
      'RESUBMISSION_REQUIRED': { color: '#fd7e14', text: 'Resubmission Required' }
    };
    
    const badge = badges[status] || badges['NOT_INITIATED'];
    return (
      <span 
        className="status-badge" 
        style={{ backgroundColor: badge.color }}
      >
        {badge.text}
      </span>
    );
  };

  return (
    <div className="merchant-kyc-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>🏪 Merchant KYC Dashboard</h1>
          <p>Complete your Know Your Customer verification to start accepting payments</p>
        </div>
        <div className="header-info" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button onClick={() => window.location.href = '/dashboard'}
            style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ← Back to Dashboard
          </button>
          <div className="merchant-info">
            <h3>{merchantProfile.personalInfo.fullName || 'Merchant'}</h3>
            <p>{merchantProfile.businessInfo.businessName || 'Business Name Not Set'}</p>
            {getStatusBadge(merchantProfile.kycStatus.status)}
          </div>
          <div className="completion-circle">
            <div className="circle-progress">
              <span className="percentage">{merchantProfile.kycStatus.completionPercentage}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        {[
          { id: 'status', label: 'KYC Status' },
          { id: 'profile', label: 'Profile Information' },
          { id: 'documents', label: 'Documents' },
          { id: 'verification', label: 'Verification' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="tab-icon">{getTabIcon(tab.id)}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'status' && (
          <MerchantKycStatus 
            merchantProfile={merchantProfile}
            onProfileUpdate={handleProfileUpdate}
            onTabChange={handleTabChange}
          />
        )}
        
        {activeTab === 'profile' && (
          <MerchantKycProfile 
            merchantProfile={merchantProfile}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
        
        {activeTab === 'documents' && (
          <MerchantKycDocuments 
            merchantProfile={merchantProfile}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
        
        {activeTab === 'verification' && (
          <MerchantKycVerification 
            merchantProfile={merchantProfile}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default MerchantKycDashboard;